<template>
  <div id="user-offers-overview" class="modal-header" style="display: block;">
    <div v-if="!data || data.length === 0" class="text-center py-5">
      <p class="text-muted mb-0">No offers data available for this user.</p>
    </div>
    <template v-else>
      <!-- Offer Name Filter -->
      <div class="mb-3">
        <b-form-input
          v-model="offerNameFilter"
          placeholder="Filter by offer name..."
          size="sm"
          class="offer-name-filter"
        />
      </div>

      <div v-if="filteredOffers.length === 0" class="text-center py-3">
        <p class="text-muted mb-0">No offers match your filter.</p>
      </div>

      <b-card
        v-for="(offer) in filteredOffers"
        :key="offer.id"
        class="mb-2"
      >
      <div class="d-flex">
        <div class="mr-1">
          <OfferElement
            :id="offer.id"
            :name="offer.name"
            :status="offer.status"
            :visibility="offer.visibility"
            :icon_url="offer.icon_url"
            class="mb-1"
          />
          <div class="d-table">
            <!-- Started -->
            <div class="mb-2">
              <strong>Started:</strong>
              {{ offer.started_at | dotDateTimeShort }}
            </div>

            <!-- Publisher Payout -->
            <div class="d-flex mb-2 align-items-center">
              <div class="text-right" style="width: 103px;">
                <strong>Publisher Payout:</strong>
              </div>
              <div class="ml-2">
                <div :class="['badge-box', getPayoutClass(offer.publisher_payout_sum)]">
                  {{ offer.publisher_payout_sum | formatCurrency }}
                </div>
              </div>
            </div>

            <!-- IAPs -->
            <div class="d-flex mb-2 align-items-center">
              <div class="text-right" style="width: 103px;">
                <strong>IAPs:</strong>
              </div>
              <div class="ml-2">
                <div
                  v-if="!!offer.has_iap_data"
                  class="badge-box badge-orange cursor-pointer"
                  @click="showIapDataModal(offer.id, offer.user_id)"
                >
                  {{ offer.transaction_value_sum | formatCurrency }}
                </div>
                <div v-else class="badge-box badge-iap-empty"> </div>
              </div>
            </div>
          </div>

        </div>
        <div class="flex-grow-1">
          <div
            v-if="countHiddenEvents(offer) > 0"
            class="mb-1"
          >
            <a href="#" @click.prevent="toggleHiddenEvents(offer.id)" style="font-size: 13px;">
              {{ showHiddenEventsPerOffer[offer.id] ? 'Hide' : 'Show' }}
              <strong>{{ countHiddenEvents(offer) }} {{ countHiddenEvents(offer) === 1 ? 'event' : 'events' }}</strong>
              without payout
            </a>
          </div>

          <b-table
            :items="getVisibleEvents(offer)"
            :fields="fields"
            small
            responsive
          >
            <template #cell(reward_name)="data">
              <div class="reward-name-cell-inner" :title="`${data.item.reward_name} (${data.item.reward_id})`">
                <div class="text-wrapper">
                <span class="truncate-text">
                  {{ data.item.reward_name }} ({{ data.item.reward_id }})
                </span>
                  <b-button
                    v-if="data.item.progress_required_event_count > 1 && data.item.events_counter"
                    class="progress-btn"
                    variant="link"
                    @click="showUserEventStepsModal(offer.user_id, data.item.reward_id)"
                  >
                    ({{ data.item.events_counter }} / {{ data.item.progress_required_event_count }})
                  </b-button>
                </div>
                <b-badge
                  v-if="data.item.is_high_risk"
                  variant="danger"
                  class="ml-1"
                >
                  High Risk
                </b-badge>
              </div>
            </template>

            <template #cell(created_at)="data">
              <template v-if="data.item.progress_required_event_count > 1 && data.item.events_counter">
                <template v-if="data.item.publisher_payout === 0"></template>
                <template v-else>
                <span v-if="data.item.step_created_at" class="text-nowrap">
                  {{ data.item.step_created_at | dotDateTimeShort }}
                </span>
                  <span v-else class="text-danger">Unknown</span>
                </template>
              </template>
              <template v-else>
                <span class="text-nowrap">{{ data.item.created_at | dotDateTimeShort }}</span>
              </template>
            </template>

            <template #cell(delay)="data">
              <template v-if="data.item.progress_required_event_count > 1 && data.item.events_counter">
                <template v-if="data.item.publisher_payout === 0">
                  <div style="padding: 5px 10px"></div>
                </template>
                <template v-else>
                  <div
                    v-if="data.item.step_delay_seconds !== null"
                    :class="getDelayColorClass(data.item.step_delay_seconds, data.item.avg_delay, data.item.p10_delay)"
                    style="padding: 5px 10px"
                  >
                    {{ data.item.step_delay }}
                  </div>
                  <div
                    v-else
                    style="padding: 5px 10px"
                  >
                    <!-- Empty div to preserve layout -->
                  </div>
                </template>
              </template>
              <template v-else>
                <div
                  :class="getDelayColorClass(data.item.delay_seconds, data.item.avg_delay, data.item.p10_delay)"
                  style="padding: 5px 10px"
                >
                  {{ data.item.delay }}
                </div>
              </template>
            </template>

            <template #cell(avg_delay)="data">
              <span v-if="hasDelayValue(data.value)">{{ formatDelayValue(data.value, data.item.avg_delay_formatted) }}</span>
            </template>

            <template #cell(p10_delay)="data">
              <span v-if="hasDelayValue(data.value)">{{ formatDelayValue(data.value, data.item.p10_delay_formatted) }}</span>
            </template>

            <template #cell(revenue)="data">
              {{ data.item.revenue | formatCurrency }}
            </template>

            <template #cell(publisher_payout)="data">
              <b-badge :class="getPayoutClass(data.item.publisher_payout)">
                {{ data.item.publisher_payout | formatCurrency }}
              </b-badge>
            </template>

            <template #cell(status)="data">
              <div class="d-flex align-items-center text-nowrap">
                <b-badge
                  :variant="getStatusVariant(data.item.status)"
                  class="text-white"
                >
                  {{ getStatusText(data.item.status) }}
                </b-badge>
              </div>
            </template>

            <template #cell(credit_status)="data">
              <EventCreditStatusElement
                :key="data.item.id"
                :status="data.item.status"
                :credit-status="data.item.credit_status"
                :credit-date="data.item.credit_date"
                :event-id="data.item.id"
                :is-hidden="data.item.is_hidden"
                :server-time="data.item.server_time"
                :disqualification-flag="data.item.disqualification_flag"
                :disqualification-reason="data.item.disqualification_reason"
                :publisher-transaction-id="data.item.publisher_transaction_id"
                :reward-type="data.item.reward_type"
                @eventUpdated="Object.assign(data.item, $event)"
                @statusChanged="data.item.status = $event"
              />
            </template>

            <template #cell(reward_change_history)="data">
              <b-button
                v-if="shouldShowRewardChangeHistory(data.item)"
                variant="outline-primary"
                size="sm"
                @click.stop="showRewardChangeHistory(data.item.reward_id)"
                title="View reward change history"
              >
                <feather-icon icon="ClockIcon" size="14" />
              </b-button>
              <span v-else class="text-muted">-</span>
            </template>
          </b-table>
        </div>
        <!-- Table of events -->
      </div>
    </b-card>
    </template>

    <!-- IAP Data Modal -->
    <b-modal id="iap-data-modal" ref="iapDataModal" title="IAP Data" hide-footer>
      <b-table
        :items="iapData"
        :fields="iapFields"
        small
        responsive
      >
        <template #cell(transaction_value)="data">
          {{ data.item.transaction_value | formatCurrency }}
        </template>
        <template #cell(transaction_time)="data">
          {{ data.item.transaction_time | dotDateTimeShort }}
        </template>
        <template #cell(provider_id)="data">
          <OfferProviderElement :id="data.item.provider_id" :name="data.item.provider_name" />
        </template>
      </b-table>
    </b-modal>

    <b-modal
      id="offer-user-event-steps-modal"
      title="User Event Steps"
      size="lg"
      hide-footer
      class="pa-2"
    >
      <table class="table table-striped">
        <thead>
        <tr>
          <th>Created At</th>
          <th>Is Paid</th>
          <th>External Transaction ID</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(step, key) in eventStepData" :key="key">
          <td>{{ step.created_at | dotDateTime }}</td>
          <td>{{ step.is_paid }}</td>
          <td>{{ step.external_transaction_id }}</td>
        </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Reward Change History Modal -->
    <RewardChangeHistoryModal
      ref="rewardChangeHistoryModal"
      :reward-id="selectedRewardId"
      modal-id="user-offers-reward-change-history-modal"
    />
  </div>
</template>

<script>
import OfferElement from '@/views/resources/Offers/Parts/OfferElement.vue'
import OfferProviderElement from '@/views/elements/OfferProviderElement.vue'
import EventCreditStatusElement from '@/views/resources/Offers/Parts/EventCreditStatusElement.vue'
import RewardChangeHistoryModal from '@/views/resources/Offers/Parts/RewardChangeHistoryModal.vue'
import { secondsToHMSReader } from '@/utils/helpers'

export default {
  name: 'UserOffersOverview',
  components: {
    EventCreditStatusElement,
    OfferProviderElement,
    OfferElement,
    RewardChangeHistoryModal
  },
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      offerNameFilter: '',
      fields: [
        { key: 'reward_name', label: 'Reward', thStyle: { width: '350px' }, tdClass: 'reward-name-cell'},
        { key: 'created_at', label: 'Completed' },
        { key: 'delay', label: 'Delay' },
        { key: 'avg_delay', label: 'Avg. Delay' },
        { key: 'p10_delay', label: 'P10 Delay' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'publisher_payout', label: 'Payout' },
        { key: 'status', label: 'Status' },
        { key: 'credit_status', label: 'Credit Status' },
        { key: 'reward_change_history', label: 'History', sortable: false }
      ],
      iapFields: [
        { key: 'transaction_value', label: 'Transaction Value' },
        { key: 'transaction_time', label: 'Transaction Time' },
        { key: 'provider_id', label: 'Provider' }
      ],
      selectedEvent: null,
      iapData: [],
      eventStepData: [],
      showHiddenEventsPerOffer: {},
      selectedRewardId: null
    }
  },
  computed: {
    canConfirmCredit () {
      return this.canAccessPageByPermissionModules(['internal.offer_user_event.credit_now'])
    },
    filteredOffers () {
      if (!this.data) return []
      if (!this.offerNameFilter.trim()) return this.data

      const searchTerm = this.offerNameFilter.toLowerCase().trim()
      return this.data.filter(offer =>
        offer.name && offer.name.toLowerCase().includes(searchTerm)
      )
    }
  },
  watch: {
    data () {
      // Reset filter when data changes (different user selected)
      this.offerNameFilter = ''
    }
  },
  methods: {
    getStatusVariant (status) {
      switch (status) {
      case 0:
        return 'warning'
      case 1:
        return 'success'
      case 2:
        return 'danger'
      case 3:
        return 'secondary'
      default:
        return 'info'
      }
    },
    getStatusText (status) {
      switch (status) {
      case 0:
        return 'Pending'
      case 1:
        return 'Completed'
      case 2:
        return 'Reconciled'
      case 3:
        return 'To Be Reconciled'
      default:
        return 'Unknown'
      }
    },
    getPayoutClass (payout) {
      if (payout < 0.30) return 'badge-light-blue'
      if (payout <= 3.50) return 'badge-medium-blue'
      return 'badge-dark-blue'
    },
    showIapDataModal (offerId, userId) {
      this.$http
        .get(`/api/user/${userId}/iap-data/${offerId}`)
        .then(response => {
          this.iapData = response.data.data
          this.$refs.iapDataModal.show()
        })
        .catch(() => {
          this.alertError('Failed to fetch IAP data.')
        })
    },
    showUserEventStepsModal (userId, rewardId) {
      this.$http
        .get(`/api/user/${userId}/event-steps/${rewardId}`)
        .then(response => {
          this.eventStepData = response.data.data
          this.$bvModal.show('offer-user-event-steps-modal')
        })
        .catch(() => {
          this.alertError('Failed to fetch IAP data.')
        })
    },
    hasDelayValue (value) {
      return value !== null && value !== '' && typeof value !== 'undefined'
    },
    formatDelayValue (value, formattedValue) {
      if (formattedValue) return formattedValue
      if (!this.hasDelayValue(value)) return ''

      return secondsToHMSReader(Number(value))
    },
    getDelayColorClass (delaySeconds, avgDelaySeconds, p10DelaySeconds) {
      if (!this.hasDelayValue(avgDelaySeconds)) return ''
      const avg = Number(avgDelaySeconds)
      const delay = Number(delaySeconds)
      const p10Delay = Number(p10DelaySeconds)

      if (Number.isNaN(avg) || Number.isNaN(delay)) return ''

      if (
        delay > ((avg * 0.5) - 600) ||
        (this.hasDelayValue(p10DelaySeconds) && !Number.isNaN(p10Delay) && delay > p10Delay)
      ) {
        return 'bg-success text-white'
      } else {
        return 'bg-warning text-white'
      }
    },
    getVisibleEvents (offer) {
      const showAll = this.showHiddenEventsPerOffer[offer.id]
      if (showAll) return offer.events

      return offer.events.filter(event => event.revenue > 0 || event.publisher_payout > 0
      )
    },
    countHiddenEvents (offer) {
      return offer.events.filter(event => event.revenue === 0 && event.publisher_payout === 0
      ).length
    },
    toggleHiddenEvents (offerId) {
      this.$set(
        this.showHiddenEventsPerOffer,
        offerId,
        !this.showHiddenEventsPerOffer[offerId]
      )
    },
    shouldShowRewardChangeHistory (item) {
      // Show button if reward_id exists
      return item && item.reward_id
    },
    showRewardChangeHistory (rewardId) {
      if (!rewardId) return
      this.selectedRewardId = rewardId
      this.$nextTick(() => {
        this.$refs.rewardChangeHistoryModal.show()
      })
    }
  }
}
</script>

<style scoped>
.badge-box {
  width: 70px;
  height: 30px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
.badge-dark-blue {
  background-color: #0047FF;
  color: white;
}
.badge-medium-blue {
  background-color: #5F73FF;
  color: white;
}
.badge-light-blue {
  background-color: #C9D4FF;
  color: #333 !important; /* or #333 for better readability */
}
.badge-orange {
  background-color: #FF9800;
}
.badge-iap-empty {
  background-color: #808080;
  position: relative;
  overflow: hidden;
  display: flex; /* Make sure it participates in flex */
  align-items: center;
  justify-content: center;
}
.badge-iap-empty::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 220%;
  height: 100%;
  border-top: 2px solid white;
  transform: rotate(30deg);
  transform-origin: center;
}
#user-offers-overview >>> .reward-name-cell {
  width: 350px !important;
  max-width: 350px;
}
.reward-name-cell-inner {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}
.text-wrapper {
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
  flex-grow: 1;
}
.truncate-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}
.progress-btn {
  margin-left: 5px;
  padding: 0;
  white-space: nowrap;
  flex-shrink: 0;
}
.offer-name-filter {
  max-width: 300px;
}
</style>
